/**
 * Tool Analytics - Client-side helper for logging tool usage
 * Sends analytics to Cloudflare Pages Functions (D1 + Laravel forwarding)
 */

export interface ToolUsageData {
  tool_name: string;
  locale: string;
  country?: string;
  referrer?: string;
  conversion_cta_shown?: boolean;
  conversion_clicked?: boolean;
  file_type?: string;
  processing_time_ms?: number;
}

export interface UblValidationData {
  locale: string;
  country?: string;
  file_name?: string;
  file_size?: number;
  is_valid: boolean;
  error_count?: number;
  warning_count?: number;
  errors?: Array<{ ruleId?: string; field: string; message: string; line?: number }>;
  warnings?: Array<{ ruleId?: string; field: string; message: string; line?: number }>;
  ubl_version?: string;
  invoice_type?: string;
  invoice_type_code?: string;
  supplier_country?: string;
  customer_country?: string;
  processing_time_ms?: number;
  invoice_id?: string;
  issue_date?: string;
  due_date?: string;
  supplier_name?: string;
  customer_name?: string;
  currency?: string;
  total_amount?: string;
  line_count?: number;
  supplier_vat?: string;
  customer_vat?: string;
  supplier_registration_name?: string;
  customer_registration_name?: string;
  supplier_endpoint_id?: string;
  customer_endpoint_id?: string;
  tax_exclusive_amount?: string;
  tax_inclusive_amount?: string;
  tax_amount?: string;
  line_extension_amount?: string;
  supplier_address_json?: string;
  customer_address_json?: string;
  line_items_json?: string;
  tax_breakdown_json?: string;
}

/**
 * Log tool usage to analytics API (Cloudflare Pages Function)
 * The Pages Function saves to D1 and forwards to Laravel API.
 * Non-blocking, fails silently to not affect user experience.
 */
export async function logToolUsage(data: ToolUsageData): Promise<void> {
  try {
    const payload: ToolUsageData = {
      ...data,
      referrer: data.referrer || (typeof window !== 'undefined' ? document.referrer : undefined),
    };

    await fetch('/api/tools/log', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    // Fail silently - analytics should never break the user experience
    console.debug('Tool usage logging failed:', error);
  }
}

/**
 * Log UBL validation result to analytics API (Cloudflare Pages Function)
 * The Pages Function saves to D1 and forwards to Laravel API.
 * Non-blocking, fails silently to not affect user experience.
 */
export async function logUblValidation(data: UblValidationData): Promise<void> {
  try {
    await fetch('/api/tools/ubl-validation', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  } catch (error) {
    // Fail silently - analytics should never break the user experience
    console.debug('UBL validation logging failed:', error);
  }
}

/**
 * Log CTA conversion event
 */
export async function logCtaConversion(tool_name: string, locale: string): Promise<void> {
  await logToolUsage({
    tool_name,
    locale,
    conversion_cta_shown: true,
    conversion_clicked: true,
  });
}

/**
 * Measure and log processing time
 */
export function createProcessingTimer() {
  const startTime = performance.now();

  return {
    getElapsed: () => Math.round(performance.now() - startTime),
  };
}
